/**
 * Ensure Professor Profile Guard (ADR-SEC-020, HU-AUTH-006).
 *
 * **El profesor es quien tiene `professor_profiles/{uid}`**: el rol de la cuenta
 * (`users.role`) sólo dice en qué app se creó y no autoriza nada. Este guard
 * asegura el perfil pendiente de cualquier cuenta que entre al admin sin él —al
 * iniciar sesión o al restaurarla—, para que, por ejemplo, un profesor que se
 * registró primero en la app del estudiante pueda construir su banco con sólo
 * entrar aquí.
 *
 * Corre DESPUÉS de `RequiresLegalConsentGuard` y **sólo actúa si el
 * consentimiento de esta cuenta quedó aceptado** en la sesión
 * (`legalConsent.status === 'accepted'` para este uid): nada se crea antes de
 * aceptar los términos. Si el guard del consentimiento dejó pasar por un fallo de
 * lectura (fail-open, sin marcar aceptado), aquí no se escribe; se reintenta en
 * la siguiente carga de la app.
 *
 * Una sola vez por uid y por carga de la app: `ensureProfessorProfile` es
 * idempotente (lee del servidor y no toca un perfil existente), pero no hace
 * falta pagarlo en cada navegación. **Nunca bloquea la navegación**: si la
 * escritura falla (p. ej. el operador del backoffice, a quien la regla niega el
 * perfil), lo registra y deja pasar — sin perfil, las reglas niegan el catálogo.
 *
 * Sustituye a `RequiresRoleGuard('professor')`, que comparaba `users.role` con un
 * `meta.role` que ninguna ruta declaraba: inerte, y con el criterio equivocado.
 * @see specs/HISTORIAS/auth-006-profesor-desde-app-estudiante.md
 */

import type { RouteLocationNormalized } from 'vue-router'
import type { RouteGuard, GuardResult } from './RouteGuard'
import { auth, authReady } from '@/services/firebase/config'
import { userService } from '@/services/userService'
import { useLegalConsentStore } from '@/stores/legalConsent'

export class EnsureProfessorProfileGuard implements RouteGuard {
  name = 'ensureProfessorProfile'

  /** uids ya intentados en esta carga de la app (con éxito o no). */
  private readonly attempted = new Set<string>()
  private readonly inFlight = new Map<string, Promise<void>>()

  async canActivate(to: RouteLocationNormalized): Promise<GuardResult> {
    if (to.meta.requiresAuth === false) return true

    await authReady
    const user = auth.currentUser
    if (!user) return true
    if (this.attempted.has(user.uid)) return true

    const consent = useLegalConsentStore()
    if (consent.status !== 'accepted' || consent.checkedUid !== user.uid) return true

    let pending = this.inFlight.get(user.uid)
    if (!pending) {
      pending = userService.ensureProfessorProfile(user)
      this.inFlight.set(user.uid, pending)
    }
    try {
      await pending
    } catch (err) {
      console.error('[ensureProfessorProfile] no se pudo asegurar el perfil de profesor', err)
    } finally {
      this.attempted.add(user.uid)
      this.inFlight.delete(user.uid)
    }
    return true
  }

  /** Olvida los intentos (cierre de sesión, pruebas). */
  reset(): void {
    this.attempted.clear()
    this.inFlight.clear()
  }
}
